import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import API from '@/lib/api';
import {
  Printer, Plus, Trash, ArrowsClockwise, DownloadSimple,
  Copy, CheckCircle, Star, Pencil, Key, WindowsLogo
} from '@phosphor-icons/react';

/* ════════════════════════════════════════════════════════════════════
   BillFac Print Agent — instellingenpaneel
   PrintNode-stijl: gebruiker downloadt Setup.exe, voert 6-cijferige
   pair-code in bij eerste run, klaar.
   ════════════════════════════════════════════════════════════════════ */

const DEFAULT_DOWNLOAD_URL = 'https://github.com/billfac/billfac-print-agent/releases/latest/download/BillFacPrintAgentSetup.exe';
const DEFAULT_RELEASES_URL = 'https://github.com/billfac/billfac-print-agent/releases/latest';

export default function PrintAgentPanel() {
  const [agents, setAgents] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pairModal, setPairModal] = useState(null); // {agent_id, agent_naam, pair_code, expires_at}
  const [tokenModal, setTokenModal] = useState(null); // {agent_id, agent_naam, agent_token} (legacy)
  const [newAgentNaam, setNewAgentNaam] = useState('');
  const [editingAlias, setEditingAlias] = useState(null);
  const [aliasInput, setAliasInput] = useState('');
  const [secsLeft, setSecsLeft] = useState(0);
  const [downloadUrl, setDownloadUrl] = useState(DEFAULT_DOWNLOAD_URL);
  const [releasesUrl, setReleasesUrl] = useState(DEFAULT_RELEASES_URL);
  const countdownTimer = useRef(null);

  const load = async () => {
    try {
      const [a, p, j, v] = await Promise.all([
        API.get('/print-agents').catch(() => ({ data: { agents: [] } })),
        API.get('/print-printers').catch(() => ({ data: { printers: [] } })),
        API.get('/print-jobs?limit=10').catch(() => ({ data: { jobs: [] } })),
        API.get('/print-agent/version/latest').catch(() => ({ data: null })),
      ]);
      setAgents(a.data?.agents || []);
      setPrinters(p.data?.printers || []);
      setJobs(j.data?.jobs || []);
      if (v.data?.download_url) setDownloadUrl(v.data.download_url);
      if (v.data?.release_notes_url) setReleasesUrl(v.data.release_notes_url);
    } catch (e) {
      toast.error('Kon print-agents niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh elke 8s indien er agents zijn
  useEffect(() => {
    if (!agents.length) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [agents.length]);

  // Countdown-timer voor pair-code modal
  useEffect(() => {
    if (countdownTimer.current) { clearInterval(countdownTimer.current); countdownTimer.current = null; }
    if (!pairModal?.expires_at) return;

    const tick = () => {
      const remain = Math.max(0, Math.floor((new Date(pairModal.expires_at).getTime() - Date.now()) / 1000));
      setSecsLeft(remain);
      if (remain <= 0 && countdownTimer.current) {
        clearInterval(countdownTimer.current);
        countdownTimer.current = null;
      }
    };
    tick();
    countdownTimer.current = setInterval(tick, 1000);
    return () => { if (countdownTimer.current) clearInterval(countdownTimer.current); };
  }, [pairModal?.expires_at]);

  // Watch: zodra agent online komt (na pairing), sluit modal automatisch
  useEffect(() => {
    if (!pairModal?.agent_id) return;
    const paired = agents.find(a => a.id === pairModal.agent_id);
    if (paired && (paired.online || paired.status === 'gekoppeld' || paired.laatst_gezien)) {
      toast.success(`✓ "${paired.naam}" is nu gekoppeld en online!`);
      setPairModal(null);
    }
  }, [agents, pairModal?.agent_id]);

  const generatePairCode = async (agentId, naam) => {
    try {
      const r = await API.post(`/print-agents/${agentId}/pair-code`);
      setPairModal({
        agent_id: agentId,
        agent_naam: naam,
        pair_code: r.data.pair_code,
        expires_at: r.data.expires_at,
      });
    } catch (e) {
      toast.error('Kon geen pair-code aanmaken: ' + (e.response?.data?.detail || e.message));
    }
  };

  const createAgent = async () => {
    const naam = (newAgentNaam || '').trim() || 'Nieuwe Agent';
    try {
      const r = await API.post('/print-agents', { naam });
      setNewAgentNaam('');
      await load();
      // Direct pair-code genereren voor een naadloze eerste-run flow
      await generatePairCode(r.data.id, r.data.naam);
    } catch (e) {
      toast.error('Aanmaken mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  const removeAgent = async (id, naam) => {
    if (!window.confirm(`Verwijder agent "${naam}" en al zijn printers?`)) return;
    try {
      await API.delete(`/print-agents/${id}`);
      toast.success('Agent verwijderd');
      load();
    } catch (e) {
      toast.error('Verwijderen mislukt');
    }
  };

  const showAdvancedToken = async (id, naam) => {
    if (!window.confirm(`Toon de rauwe token voor "${naam}"?\n\nAlleen nodig voor gevorderde setups (bv. Linux/macOS agent).\nStandaardgebruikers moeten de pair-code + Setup.exe gebruiken.`)) return;
    try {
      const r = await API.post(`/print-agents/${id}/rotate-token`);
      setTokenModal({ agent_id: id, agent_naam: naam, agent_token: r.data.agent_token });
      load();
    } catch (e) {
      toast.error('Token rotatie mislukt');
    }
  };

  const setDefault = async (printerId) => {
    try {
      await API.put(`/print-printers/${printerId}`, { is_default: true });
      toast.success('Standaard-printer ingesteld');
      load();
    } catch (e) {
      toast.error('Mislukt');
    }
  };

  const saveAlias = async (printerId) => {
    try {
      await API.put(`/print-printers/${printerId}`, { alias: aliasInput });
      setEditingAlias(null);
      toast.success('Bijnaam opgeslagen');
      load();
    } catch (e) {
      toast.error('Mislukt');
    }
  };

  const removePrinter = async (id, naam) => {
    if (!window.confirm(`Verwijder "${naam}"?\n\n(De printer verschijnt opnieuw bij de volgende scan van de agent.)`)) return;
    try {
      await API.delete(`/print-printers/${id}`);
      load();
    } catch (e) {
      toast.error('Mislukt');
    }
  };

  const copyToClipboard = (text, label = 'Code') => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} gekopieerd naar klembord`),
      () => toast.error('Kopiëren mislukt')
    );
  };

  const formatSecsLeft = () => {
    if (secsLeft <= 0) return '⏱ verlopen';
    const m = Math.floor(secsLeft / 60);
    const s = secsLeft % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <div className="text-xs text-gray-500 p-4">Laden…</div>;
  }

  return (
    <div className="space-y-4" data-testid="print-agent-panel">
      {/* ── Header met download-knop ── */}
      <div className="p-4 bg-[#EFF6FF] border-2 border-[#2563EB]/30 text-xs text-[#1E40AF]">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="font-bold text-sm mb-1">🖨️ BillFac Print Agent — Remote printing</div>
            <div>Druk uw documenten af op uw kantoor-printers in België, óók wanneer u in het buitenland werkt. Installeer onze gratis Windows-app op uw PC — auto-detectie van al uw printers, geen configuratie nodig.</div>
          </div>
          <a
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#002FA7] text-white text-sm font-bold hover:bg-[#001E6E] whitespace-nowrap"
            data-testid="download-windows-agent-btn"
          >
            <WindowsLogo size={18} weight="fill" />
            Windows agent downloaden
          </a>
        </div>
      </div>

      {/* ── Pair-code modal (nieuwe UX) ── */}
      {pairModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 max-w-md w-full border-2 border-[#002FA7] shadow-xl" data-testid="pair-code-modal">
            <div className="flex items-center gap-2 mb-3">
              <Key size={22} weight="fill" className="text-[#002FA7]" />
              <div className="font-bold text-base">Koppel &quot;{pairModal.agent_naam}&quot;</div>
            </div>
            <div className="text-xs text-gray-600 mb-4">
              Voer deze code in de <strong>BillFac Print Agent</strong>-app op uw Windows PC:
            </div>

            <div className="bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE] border-2 border-[#2563EB] p-6 mb-4 text-center">
              <div className="font-mono text-5xl font-black tracking-[0.4em] text-[#002FA7] select-all" data-testid="pair-code-value">
                {pairModal.pair_code}
              </div>
              <div className="mt-3 text-[10px] uppercase font-bold text-gray-500">
                geldig nog {formatSecsLeft()}
              </div>
            </div>

            <div className="p-3 bg-[#FFFBEB] border border-[#F59E0B]/40 text-[11px] text-[#78350F] mb-3">
              <strong>Nog geen agent-app?</strong>
              <a href={downloadUrl} target="_blank" rel="noreferrer"
                 className="text-[#002FA7] underline ml-1 font-semibold">
                Download BillFacPrintAgentSetup.exe
              </a>{' '}en installeer hem (30 sec).
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                className="px-3 py-2 bg-[#002FA7] text-white text-xs font-bold hover:bg-[#001E6E]"
                onClick={() => copyToClipboard(pairModal.pair_code, 'Pair-code')}
                data-testid="copy-pair-code-btn"
              >
                <Copy size={14} className="inline mr-1" /> Kopieer code
              </button>
              <button
                type="button"
                className="px-3 py-2 bg-white border-2 border-[#002FA7] text-[#002FA7] text-xs font-bold hover:bg-[#EFF6FF]"
                onClick={() => generatePairCode(pairModal.agent_id, pairModal.agent_naam)}
                data-testid="regenerate-pair-code-btn"
              >
                <ArrowsClockwise size={14} className="inline mr-1" /> Nieuwe code
              </button>
              <button
                type="button"
                className="ml-auto px-3 py-2 bg-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-300"
                onClick={() => setPairModal(null)}
                data-testid="close-pair-modal-btn"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Advanced token modal (legacy — voor Python-script gebruikers) ── */}
      {tokenModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-5 max-w-lg w-full border-2 border-[#DC2626] shadow-xl" data-testid="token-modal">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={20} weight="fill" className="text-[#059669]" />
              <div className="font-bold text-base">Rauwe token voor &quot;{tokenModal.agent_naam}&quot;</div>
            </div>
            <div className="p-3 bg-[#FEE2E2] border border-[#DC2626]/40 text-xs text-[#991B1B] mb-3">
              <strong>⚠️ Gevorderde setup:</strong> gebruik deze token enkel voor eigen scripts of Linux/macOS agents.
              De token wordt slechts één keer getoond — bewaar hem veilig.
            </div>
            <div className="bg-gray-50 p-2 border border-gray-200 font-mono text-xs break-all select-all">
              {tokenModal.agent_token}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <button
                type="button"
                className="px-3 py-1.5 bg-[#002FA7] text-white text-xs font-bold hover:bg-[#001E6E]"
                onClick={() => copyToClipboard(tokenModal.agent_token, 'Token')}
                data-testid="copy-raw-token-btn"
              >
                <Copy size={14} className="inline mr-1" /> Kopieer token
              </button>
              <button
                type="button"
                className="ml-auto px-3 py-1.5 bg-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-300"
                onClick={() => setTokenModal(null)}
                data-testid="close-token-modal"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Nieuwe agent aanmaken ── */}
      <div className="border-2 border-dashed border-gray-300 p-3 flex items-end gap-2 bg-gray-50/50">
        <div className="flex-1">
          <label className="form-label">Naam voor nieuwe PC</label>
          <input
            type="text"
            className="field-input"
            placeholder="bv. 'Kantoor PC België' of 'Magazijn Brother'"
            value={newAgentNaam}
            onChange={e => setNewAgentNaam(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') createAgent(); }}
            data-testid="new-agent-naam-input"
          />
        </div>
        <button
          type="button"
          className="px-4 py-2 bg-[#002FA7] text-white text-xs font-bold hover:bg-[#001E6E]"
          onClick={createAgent}
          data-testid="create-agent-btn"
        >
          <Plus size={14} className="inline mr-1" /> Koppel nieuwe PC
        </button>
      </div>

      {/* ── Lijst van agents ── */}
      {agents.length === 0 ? (
        <div className="text-center py-6 text-xs text-gray-500 border border-gray-200 bg-white">
          Nog geen print-agents gekoppeld. Klik hierboven op <strong>&quot;Koppel nieuwe PC&quot;</strong> om te starten.
        </div>
      ) : (
        <div className="space-y-2">
          {agents.map(a => (
            <div key={a.id} className="border-2 border-gray-200 bg-white p-3" data-testid={`agent-row-${a.id}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <Printer size={18} weight="duotone" className={a.online ? 'text-[#059669]' : 'text-gray-400'} />
                  <div>
                    <div className="font-bold text-sm">{a.naam}</div>
                    <div className="text-[10px] text-gray-500">
                      {a.platform || 'Nog niet geconnecteerd'}
                      {a.versie ? ` · v${a.versie}` : ''}
                      {a.laatst_gezien ? ` · laatst online: ${new Date(a.laatst_gezien).toLocaleString('nl-BE')}` : ''}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-2 py-0.5 font-bold uppercase ${a.online ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-gray-200 text-gray-600'}`}>
                    {a.online ? '● online' : 'offline'}
                  </span>
                  <button
                    type="button"
                    className="px-2 py-1 bg-[#EFF6FF] border border-[#2563EB] text-[#1E40AF] text-[10px] font-bold hover:bg-[#DBEAFE]"
                    onClick={() => generatePairCode(a.id, a.naam)}
                    title="Genereer een nieuwe pair-code om deze PC opnieuw te koppelen"
                    data-testid={`pair-again-${a.id}`}
                  >
                    <Key size={11} className="inline mr-0.5" /> Herkoppel
                  </button>
                  <button
                    type="button"
                    className="px-2 py-1 bg-[#FEE2E2] text-[#DC2626] text-[10px] font-bold hover:bg-[#FCA5A5]"
                    onClick={() => removeAgent(a.id, a.naam)}
                    title="Verwijder agent"
                    data-testid={`delete-agent-${a.id}`}
                  >
                    <Trash size={11} />
                  </button>
                </div>
              </div>

              {/* Printers van deze agent */}
              {(() => {
                const agentPrinters = printers.filter(p => p.agent_id === a.id);
                if (agentPrinters.length === 0) {
                  return (
                    <div className="ml-7 text-[11px] text-gray-500 italic bg-gray-50 p-2 border border-gray-100">
                      Geen printers ontdekt. Start de agent op uw PC om printers automatisch te scannen.
                    </div>
                  );
                }
                return (
                  <div className="ml-7 space-y-1">
                    <div className="text-[10px] font-bold uppercase text-gray-500 mb-1">
                      🖨️ {agentPrinters.length} printer{agentPrinters.length > 1 ? 's' : ''} ontdekt
                    </div>
                    {agentPrinters.map(p => (
                      <div key={p.id} className="flex items-center gap-2 text-xs bg-gray-50 px-2 py-1.5 border border-gray-100" data-testid={`printer-row-${p.id}`}>
                        <span className={`w-2 h-2 rounded-full ${p.status === 'online' ? 'bg-[#10B981]' : 'bg-gray-400'}`}></span>
                        {editingAlias === p.id ? (
                          <div className="flex-1 flex items-center gap-1">
                            <input
                              type="text"
                              className="field-input flex-1 text-[11px] py-0.5"
                              value={aliasInput}
                              onChange={e => setAliasInput(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') saveAlias(p.id); if (e.key === 'Escape') setEditingAlias(null); }}
                              placeholder="Bijnaam (bv. 'Boven', 'Magazijn')"
                              autoFocus
                              data-testid={`alias-input-${p.id}`}
                            />
                            <button type="button" className="px-2 py-0.5 bg-[#059669] text-white text-[10px]" onClick={() => saveAlias(p.id)} data-testid={`save-alias-${p.id}`}>OK</button>
                            <button type="button" className="px-2 py-0.5 bg-gray-200 text-[10px]" onClick={() => setEditingAlias(null)} data-testid={`cancel-alias-${p.id}`}>×</button>
                          </div>
                        ) : (
                          <>
                            <span className="font-semibold flex-1">
                              {p.alias ? <><span className="text-[#002FA7]">{p.alias}</span> <span className="text-gray-400 text-[10px]">({p.naam})</span></> : p.naam}
                            </span>
                            <span className="text-[9px] uppercase px-1.5 py-0.5 bg-gray-200 text-gray-700">{p.type}</span>
                            {p.is_system_default && <span className="text-[9px] uppercase px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E]">PC default</span>}
                            {p.is_default && <span className="text-[9px] uppercase px-1.5 py-0.5 bg-[#DBEAFE] text-[#1E40AF]"><Star size={9} weight="fill" className="inline mr-0.5" />BillFac default</span>}
                            <button
                              type="button"
                              className="text-[10px] text-[#002FA7] hover:underline"
                              onClick={() => { setEditingAlias(p.id); setAliasInput(p.alias || ''); }}
                              data-testid={`edit-alias-${p.id}`}
                            >
                              <Pencil size={10} className="inline" /> bijnaam
                            </button>
                            {!p.is_default && (
                              <button
                                type="button"
                                className="text-[10px] text-[#059669] hover:underline"
                                onClick={() => setDefault(p.id)}
                                data-testid={`set-default-${p.id}`}
                              >
                                set default
                              </button>
                            )}
                            <button
                              type="button"
                              className="text-[10px] text-[#DC2626] hover:underline"
                              onClick={() => removePrinter(p.id, p.naam)}
                              data-testid={`delete-printer-${p.id}`}
                            >
                              <Trash size={10} className="inline" />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {/* ── Recente print jobs ── */}
      {jobs.length > 0 && (
        <details className="border border-gray-200 bg-white">
          <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-gray-700 hover:bg-gray-50 select-none">
            📋 Laatste {jobs.length} print-jobs
          </summary>
          <div className="px-3 pb-3 space-y-1">
            {jobs.map(j => (
              <div key={j.id} className="flex items-center gap-2 text-[11px] py-1 border-t border-gray-100" data-testid={`job-row-${j.id}`}>
                <span className={`w-2 h-2 rounded-full ${
                  j.status === 'voltooid' ? 'bg-[#10B981]' :
                  j.status === 'wachtend' ? 'bg-[#F59E0B]' :
                  j.status === 'bezig' ? 'bg-[#3B82F6] animate-pulse' :
                  'bg-[#EF4444]'
                }`}></span>
                <span className="font-mono text-[10px] text-gray-500 w-20 truncate">{j.id}</span>
                <span className="flex-1 truncate">{j.bestandsnaam}</span>
                <span className="text-gray-500">→ {j.printer_naam}</span>
                <span className={`uppercase font-bold text-[9px] px-1.5 py-0.5 ${
                  j.status === 'voltooid' ? 'bg-[#D1FAE5] text-[#065F46]' :
                  j.status === 'wachtend' ? 'bg-[#FEF3C7] text-[#92400E]' :
                  j.status === 'bezig' ? 'bg-[#DBEAFE] text-[#1E40AF]' :
                  'bg-[#FEE2E2] text-[#991B1B]'
                }`}>{j.status}</span>
                <span className="text-gray-400">{new Date(j.created_at).toLocaleTimeString('nl-BE')}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* ── Installatie-instructies ── */}
      <details className="border border-gray-200 bg-gray-50/50">
        <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-gray-700 hover:bg-gray-100 select-none">
          📖 Installatie-instructies (30 seconden)
        </summary>
        <div className="px-4 py-3 space-y-2 text-xs text-gray-700">
          <div><strong>Stap 1.</strong> Klik hierboven op <a href={downloadUrl} target="_blank" rel="noreferrer" className="text-[#002FA7] underline font-semibold">Windows agent downloaden</a>. <code className="bg-gray-200 px-1">BillFacPrintAgentSetup.exe</code> wordt gedownload.</div>
          <div><strong>Stap 2.</strong> Dubbelklik op <code className="bg-gray-200 px-1">BillFacPrintAgentSetup.exe</code> en volg <em>&quot;Volgende, Volgende, Voltooien&quot;</em>. Geen admin-rechten vereist.</div>
          <div><strong>Stap 3.</strong> De app opent automatisch en vraagt om een <strong>pair-code</strong>.</div>
          <div><strong>Stap 4.</strong> Klik hierboven op <em>&quot;Koppel nieuwe PC&quot;</em>, kopieer de 6-cijferige code, plak hem in de app en klik &quot;Koppelen&quot;.</div>
          <div><strong>Stap 5.</strong> Klaar! Het BillFac-icoon in uw systeembalk wordt groen. Uw printers worden automatisch gedetecteerd en verschijnen hierboven binnen 30 sec.</div>
          <div className="p-2 bg-[#D1FAE5] border border-[#059669]/40 text-[11px] text-[#065F46]">
            ✅ De app start voortaan automatisch mee met Windows. Uw PC moet aan staan én verbonden met internet om remote te kunnen printen.
          </div>
          <div className="text-[10px] text-gray-500 pt-2 border-t border-gray-200 flex items-center gap-2 flex-wrap">
            <a href={releasesUrl} target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Release notes</a>
            <span>·</span>
            <button
              type="button"
              className="text-[#DC2626] underline text-[10px]"
              onClick={() => {
                if (agents.length === 0) {
                  toast.error('Maak eerst een agent aan (Koppel nieuwe PC).');
                  return;
                }
                const a = agents[0];
                showAdvancedToken(a.id, a.naam);
              }}
              data-testid="show-raw-token-btn"
            >
              Gevorderd: rauwe token tonen (Linux/macOS)
            </button>
          </div>
        </div>
      </details>
    </div>
  );
}
